import type { DealTransformed } from "@/lib/schema/deal-schema";

const PLACEHOLDERS = [
  "no information provided",
  "no information is provided",
  "not specified",
  "n/a",
];

const isPlaceholder = (v: unknown): boolean =>
  typeof v === "string" &&
  (!v.trim() || PLACEHOLDERS.includes(v.trim().toLowerCase()));

// Scoped to `sub_industries` — the only industry field the Profile tab
// renders as an editable / verifiable row. Older deals can carry pending
// `industries` or `industry` entries in deal_info that the user has no way
// to approve from the UI, so surfacing them as "Review industries" badges
// produced a dead-end click: pipeline says "review needed" → user opens
// profile → everything is already approved → badge still there next time
// they come back. Aligning the badge with the user-actionable field keeps
// the two surfaces consistent. Use `getLegacyPendingIndustryEntries` below
// to surface the dead-end entries in diagnostics / logs.
const INDUSTRY_FIELDS = ["sub_industries"];
const LEGACY_INDUSTRY_FIELDS = ["industries", "industry"];

// True when the user-actionable industry field on `deal_info` carries
// `field_state === "pending"` and a non-placeholder value. Placeholder
// values ("n/a", "not specified", …) are treated as absent so reviewers
// aren't nagged to approve nothing.
export function isIndustriesPendingApproval(
  deal: Pick<DealTransformed, "dealInfo">,
): boolean {
  const items = deal.dealInfo?.deal ?? [];
  return INDUSTRY_FIELDS.some((name) => {
    const item = items.find((i) => i.field_name === name);
    if (!item) return false;
    if (item.field_state?.toLowerCase() !== "pending") return false;
    const value = item.field_value;
    if (value == null) return false;
    if (typeof value === "string") return !isPlaceholder(value);
    if (Array.isArray(value)) {
      return value.length > 0 && !value.every(isPlaceholder);
    }
    return true;
  });
}

// Returns the legacy `industries` / `industry` entries on `deal_info` that
// are pending in BE but unreachable from the FE (no editable row renders
// them). Intended for diagnostic logging so we can track down deals
// carrying stale pending state and clean them up upstream.
export function getLegacyPendingIndustryEntries(
  deal: Pick<DealTransformed, "dealInfo">,
): Array<{ field_name: string; field_value: unknown }> {
  const items = deal.dealInfo?.deal ?? [];
  const out: Array<{ field_name: string; field_value: unknown }> = [];
  for (const name of LEGACY_INDUSTRY_FIELDS) {
    const item = items.find((i) => i.field_name === name);
    if (!item) continue;
    if (item.field_state?.toLowerCase() !== "pending") continue;
    const value = item.field_value;
    if (value == null) continue;
    if (typeof value === "string" && isPlaceholder(value)) continue;
    if (
      Array.isArray(value) &&
      (value.length === 0 || value.every(isPlaceholder))
    ) {
      continue;
    }
    out.push({ field_name: name, field_value: value });
  }
  return out;
}
